"use client";

import { useEffect, useRef } from "react";

interface AboutSectionProps {
  className?: string;
}

export default function AboutSection({ className = "" }: AboutSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-item").forEach((el, i) => {
              (el as HTMLElement).style.animationDelay = `${i * 0.12}s`;
              el.classList.add("animate-fade-up");
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className={`py-32 md:py-40 bg-white ${className}`} id="about">
      <div className="max-w-5xl mx-auto px-8 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="reveal-item opacity-100 flex items-center justify-center gap-3 mb-6">
            <span className="w-10 h-px bg-gold" />
            <span className="text-xs font-semibold tracking-luxury text-gold uppercase">About Us</span>
            <span className="w-10 h-px bg-gold" />
          </div>
          <h2 className="reveal-item opacity-100 font-serif text-4xl md:text-5xl lg:text-6xl font-normal leading-tight text-charcoal">
            The Story Behind
            <span className="block italic font-normal text-gold mt-2">HÅR BY SCANDINAVIA</span>
          </h2>
        </div>

        {/* Story Content */}
        <div className="space-y-8 text-center max-w-4xl mx-auto">
          <p className="reveal-item opacity-100 text-lg md:text-xl text-charcoal font-light leading-relaxed">
            Steffi grew up in Denmark, shaped by the calm, clean aesthetics of Scandinavia and the belief that true beauty never needs to be loud.
          </p>

          <p className="reveal-item opacity-100 text-lg md:text-xl text-muted font-light leading-relaxed">
            With over 12 years of experience in the beauty industry and several successful salon locations in Denmark and Germany, she has refined her craft to the highest standard. Her philosophy has always remained the same: preserve natural beauty, enhance individuality, and create confidence through subtle elegance.
          </p>

          <p className="reveal-item opacity-100 text-lg md:text-xl text-muted font-light leading-relaxed">
            Before fully dedicating herself to the beauty world, Steffi studied Social Counseling in Denmark. This foundation continues to influence her work today. For her, it is not only about hair — it is about listening, understanding, and creating a personalized experience for every woman who sits in her chair. Every consultation begins with attention. Every transformation begins with trust.
          </p>

          <p className="reveal-item opacity-100 text-lg md:text-xl text-muted font-light leading-relaxed">
            As a native Dane, she speaks Danish, German, and English fluently, welcoming an international clientele with warmth and professionalism.
          </p>

          <p className="reveal-item opacity-100 text-lg md:text-xl text-muted font-light leading-relaxed">
            We do not glue hair extensions. Our method is gentle and hair-friendly, preserving the natural health and integrity of your hair.
          </p>

          <div className="reveal-item opacity-100 pt-8">
            <div className="inline-block p-8 bg-sand/30 rounded-[1.75rem] border border-warm">
              <p className="text-lg md:text-xl text-charcoal font-light leading-relaxed mb-4">
                With <span className="font-semibold text-gold">HÅR BY SCANDINAVIA</span>, Steffi brings Nordic craftsmanship and Scandinavian refinement to Dubai — clean, sophisticated, and timeless.
              </p>
              <p className="text-xl md:text-2xl font-serif italic text-gold">
                Here, hair is not simply extended. It is elevated.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}